/**
 * The single QueryClient and the shared key factory.
 *
 * Analytics answers do not change second to second, so nothing refetches on window focus and
 * a failed request is retried once — never on a 4xx, which is a contract problem, not a blip.
 */

import { QueryClient } from '@tanstack/react-query'
import { ApiError } from './api'

const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 1) return false
  if (error instanceof ApiError) {
    // Auth, validation and quota answers are stable — retrying only wastes a round trip.
    if (error.status >= 400 && error.status < 500) return false
  }
  return true
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      throwOnError: false,
    },
    mutations: {
      retry: false,
    },
  },
})

/** Wipes every cached answer — called on sign-out and after switching organisation. */
export const resetQueryCache = (): void => {
  queryClient.clear()
}

export interface RangeKey {
  from: string
  to: string
}

/**
 * Key factory. Every feature agent builds keys from here so an invalidation in one screen
 * reaches the same rows in another. Keys are always `['pd', <area>, ...]`.
 */
export const qk = {
  session: () => ['pd', 'session'] as const,
  branding: () => ['pd', 'branding'] as const,
  providers: () => ['pd', 'providers'] as const,

  brands: () => ['pd', 'brands'] as const,
  brand: (blogId: number) => ['pd', 'brands', blogId] as const,
  connections: (blogId: number) => ['pd', 'brands', blogId, 'connections'] as const,

  dashboard: (blogId: number, range: RangeKey) =>
    ['pd', 'dashboard', blogId, range.from, range.to] as const,
  summary: (blogId: number, range: RangeKey) =>
    ['pd', 'analytics', 'summary', blogId, range.from, range.to] as const,
  timeline: (blogId: number, provider: string, metric: string, range: RangeKey) =>
    ['pd', 'analytics', 'timeline', blogId, provider, metric, range.from, range.to] as const,
  distribution: (blogId: number, provider: string, kind: string, range: RangeKey) =>
    ['pd', 'analytics', 'distribution', blogId, provider, kind, range.from, range.to] as const,
  postsAnalytics: (blogId: number, provider: string, range: RangeKey) =>
    ['pd', 'analytics', 'posts', blogId, provider, range.from, range.to] as const,
  bestTimes: (blogId: number, provider: string) =>
    ['pd', 'analytics', 'best-times', blogId, provider] as const,
  hashtags: (blogId: number, provider: string, range: RangeKey) =>
    ['pd', 'analytics', 'hashtags', blogId, provider, range.from, range.to] as const,

  /* Campaigns: a grouping of posts, so keyed by brand and never by a date range. The performance
     read is the one exception — it takes an optional window, and `null` means "every post of this
     campaign", which is a different answer and therefore a different cache entry. */
  campaigns: (blogId: number) => ['pd', 'campaigns', blogId] as const,
  campaign: (blogId: number, campaignId: string) =>
    ['pd', 'campaigns', blogId, campaignId] as const,
  campaignPerformance: (blogId: number, campaignId: string, range: RangeKey | null) =>
    ['pd', 'campaigns', blogId, campaignId, 'performance', range?.from ?? 'all', range?.to ?? 'all'] as const,
  campaignSuggestions: (blogId: number, hashtag: string, campaignId: string | null) =>
    ['pd', 'campaigns', blogId, 'suggestions', hashtag, campaignId ?? 'none'] as const,
  campaignAssignment: (blogId: number, plannerPostId: number) =>
    ['pd', 'campaigns', blogId, 'assignment', plannerPostId] as const,

  /* The workflow board: an arrangement of a brand's posts, so keyed by brand and never by a date
     range. The board read carries its own columns, but the column editor and the assignee picker
     are separate entries — both outlive a single board render and neither should refetch the cards
     to answer a question about a name. */
  workflowBoard: (blogId: number) => ['pd', 'workflow', blogId, 'board'] as const,
  workflowStates: (blogId: number) => ['pd', 'workflow', blogId, 'states'] as const,
  workflowAssignees: (blogId: number) => ['pd', 'workflow', blogId, 'assignees'] as const,

  /* Flows: a brand's standing automations, and the history of what each one did. The history is
     keyed by page and status filter as well as by flow, because those are different answers and a
     shared entry would make paging overwrite the page somebody came from. */
  flows: (blogId: number) => ['pd', 'flows', blogId] as const,
  flowExecutions: (blogId: number, flowId: string, page: number, status: string) =>
    ['pd', 'flows', blogId, flowId, 'executions', page, status] as const,
  /* The posts a rule may name. Deliberately **outside** the `flows` prefix: `invalidateQueries`
     matches by prefix, and writing a Flow does not change which posts exist — a shared prefix
     would refetch the planner's drafts every time somebody flipped a toggle. */
  flowPostOptions: (blogId: number) => ['pd', 'flowPosts', blogId] as const,

  /* Automazioni: same shape as Flows, for the identical reasons — a separate prefix for the
     drafts a `PUBLISH_POST` node may name, keyed history by page and status. */
  automations: (blogId: number) => ['pd', 'automations', blogId] as const,
  automationExecutions: (blogId: number, ruleId: string, page: number, status: string) =>
    ['pd', 'automations', blogId, ruleId, 'executions', page, status] as const,
  automationDrafts: (blogId: number) => ['pd', 'automationDrafts', blogId] as const,
  automationPublished: (blogId: number) => ['pd', 'automationPublished', blogId] as const,

  /* Content drafting: a brand's watched feeds, its opted-in competitors, and the proposals both
     produced. The proposal list is keyed by its filters as well as by brand, because "waiting for
     review" and "everything this source ever produced" are different answers and a shared entry
     would show one where the other was asked for. All three sit under one `drafts` prefix on
     purpose — accepting a proposal changes a source's pending count, so one invalidation is meant
     to refresh every one of them. */
  draftFeedSources: (blogId: number) => ['pd', 'drafts', blogId, 'feeds'] as const,
  draftCompetitorSources: (blogId: number) => ['pd', 'drafts', blogId, 'competitors'] as const,
  draftProposals: (blogId: number, status: string, kind: string) =>
    ['pd', 'drafts', blogId, 'proposals', status, kind] as const,

  /* Evergreen recycling: the brand's own past posts a daily pass thinks are worth running again.
     Its **own** prefix rather than a branch of `drafts`, for the reason the two features have
     separate tables — nothing a reviewer does to a draft proposal changes this list, and sharing a
     prefix would make every accept and dismiss refetch a list that did not move. Keyed by status
     as well as brand, because "waiting for me" and "what I dismissed" are different answers. */
  evergreen: (blogId: number, status: string) =>
    ['pd', 'evergreen', blogId, status] as const,

  /* The onboarding checklist: recomputed by the server on every read, so the entry holds a
     reading rather than a resource, and is keyed by brand alone — the milestones are facts about
     the brand, and the two per-viewer flags ride in the same answer because they come from the
     same request. No date range: "has this brand ever" is not a windowed question. */
  onboarding: (blogId: number) => ['pd', 'onboarding', blogId] as const,

  planner: (blogId: number, range: RangeKey) =>
    ['pd', 'planner', blogId, range.from, range.to] as const,
  post: (postId: string) => ['pd', 'planner', 'post', postId] as const,
  plannerCounters: (blogId: number) => ['pd', 'planner', 'counters', blogId] as const,
  /** The Pinterest boards a Pin can be created on. Per brand, because the grant is. */
  pinterestBoards: (blogId: number) => ['pd', 'planner', 'pinterest-boards', blogId] as const,
  tiktokCreatorInfo: (blogId: number) => ['pd', 'planner', 'tiktok-creator-info', blogId] as const,
  media: (blogId: number) => ['pd', 'media', blogId] as const,

  inbox: (blogId: number, filter: string) => ['pd', 'inbox', blogId, filter] as const,
  conversation: (id: string) => ['pd', 'inbox', 'conversation', id] as const,

  /* The bell. Keyed by nothing but the page, because a notification belongs to the *person* and
     not to a workspace or a brand: switching either must not throw the feed away, and the count
     that the shell polls is the same number in every workspace. */
  notifications: (page: number) => ['pd', 'notifications', 'feed', page] as const,
  notificationsUnread: () => ['pd', 'notifications', 'unread'] as const,
  notificationPreferences: () => ['pd', 'notifications', 'preferences'] as const,

  members: (orgId: string) => ['pd', 'org', orgId, 'members'] as const,
  /* Referrals belong to the workspace, so the key carries no org id for the same reason
     `subscription()` does not: `X-Org-Id` decides the answer and switching workspaces resets the
     whole cache. */
  referrals: () => ['pd', 'referrals'] as const,
  plans: () => ['pd', 'billing', 'plans'] as const,
  subscription: () => ['pd', 'billing', 'subscription'] as const,
  whitelabel: () => ['pd', 'whitelabel'] as const,

  adminOverview: () => ['pd', 'admin', 'overview'] as const,
  adminOrgs: (query: string, page: number) => ['pd', 'admin', 'orgs', query, page] as const,
  adminOrg: (orgId: string) => ['pd', 'admin', 'orgs', orgId] as const,
  adminWhitelabelPartners: () => ['pd', 'admin', 'whitelabels'] as const,
  adminUsers: (query: string, page: number) => ['pd', 'admin', 'users', query, page] as const,
  adminPlans: () => ['pd', 'admin', 'plans'] as const,
  adminAudit: (page: number) => ['pd', 'admin', 'audit', page] as const,
  adminDevHealth: () => ['pd', 'admin', 'dev', 'health'] as const,
} as const
